import React from "react";
import * as axios from "axios";
import {
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreIcon from "@material-ui/icons/Restore";
import ScheduleIcon from "@material-ui/icons/Schedule";
import RoomIcon from "@material-ui/icons/Room";
import {loadGoogleMaps} from "../../lib/googleMaps";
import ImageLightbox from "../../Components/ImageLightbox";
import "./styles.css";

const CLOSED_DAY_LABEL = "Închis";

const SCHEDULE_DAYS = [
  {key: 'monday', label: 'Luni'},
  {key: 'tuesday', label: 'Marți'},
  {key: 'wednesday', label: 'Miercuri'},
  {key: 'thursday', label: 'Joi'},
  {key: 'friday', label: 'Vineri'},
  {key: 'saturday', label: 'Sâmbătă'},
  {key: 'sunday', label: 'Duminică'},
];

// A restaurant's tablet is considered offline once it's gone this long
// without polling for new orders (see the backend's OrderController.
// ListPending, hit every ~10s while the tablet app is actually running).
const ONLINE_THRESHOLD_SECONDS = 5 * 60;

// Anything within one missed poll's worth of margin (the tablet polls
// every ~10s) just reads as "Online" rather than a specific age.
const FRESH_THRESHOLD_SECONDS = 10;

function lastSeenSecondsAgo(lastSeen) {
  if (!lastSeen) {
    return null;
  }
  return Math.max(0, Math.round((Date.now() - new Date(lastSeen)) / 1000));
}

function isRestaurantFreshlyOnline(lastSeen) {
  const seconds = lastSeenSecondsAgo(lastSeen);
  return seconds !== null && seconds <= FRESH_THRESHOLD_SECONDS;
}

// "acum X" for how long ago a restaurant's tablet last polled for orders —
// same phrasing convention as Map's formatCacheAge, extended with a days
// tier since a tablet can plausibly be offline for a while, not just
// stale-refreshed like a live GPS cache.
function formatLastSeen(lastSeen) {
  const seconds = lastSeenSecondsAgo(lastSeen);
  if (seconds === null) {
    return 'Niciodată';
  }
  if (seconds < 60) {
    return `acum ${seconds} secunde`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `acum ${minutes} ${minutes === 1 ? 'minut' : 'minute'}`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `acum ${hours} ${hours === 1 ? 'oră' : 'ore'}`;
  }
  const days = Math.round(hours / 24);
  return `acum ${days} ${days === 1 ? 'zi' : 'zile'}`;
}

function isRestaurantOffline(lastSeen) {
  if (!lastSeen) {
    return true;
  }
  return (Date.now() - new Date(lastSeen)) / 1000 > ONLINE_THRESHOLD_SECONDS;
}

function normalizeTime(value) {
  const [h, m] = (value || '').trim().split(':');
  return `${(h || '0').padStart(2, '0')}:${(m || '0').padStart(2, '0')}`;
}

function parseScheduleValue(value) {
  if (!value || value === CLOSED_DAY_LABEL) {
    return {closed: true, open: '10:00', close: '22:00'};
  }
  const [open, close] = value.split('-');
  if (!open || !close) {
    return {closed: true, open: '10:00', close: '22:00'};
  }
  return {closed: false, open: normalizeTime(open), close: normalizeTime(close)};
}

const DEFAULT_MAP_CENTER = {lat: 46.5632507, lng: 23.7843233}; // Turda

const ADDRESS_FIELDS_COL2 = [
  {key: 'street', label: 'Stradă'},
  {key: 'number', label: 'Număr'},
  {key: 'apartment', label: 'Apartament'},
];

const ADDRESS_FIELDS_COL3 = [
  {key: 'city', label: 'Oraș'},
  {key: 'county', label: 'Județ'},
  {key: 'city_id', label: 'ID Oraș', type: 'number'},
];

const ADDRESS_FIELDS = [...ADDRESS_FIELDS_COL2, ...ADDRESS_FIELDS_COL3];

// minimum_order_price, delivery_time, delivery_fee, free_delivery_over,
// app_tax and open_hours are deliberately excluded: they're overwritten from
// delivery_areas / restaurants_schedules at read time, so editing them here
// would be misleading. open_hours is managed via the "Setează program"
// action instead. description is rendered separately, under the name field.
// ebriza_client_id is rendered separately too, gated behind the "Integrare
// Ebriza" checkbox. company_id is rendered as a company picker. oblio_cif is
// hidden entirely — the backend mirrors it from the selected company's CIF.
const TEXT_FIELDS = [
  {key: 'phone', label: 'Telefon'},
  {key: 'email', label: 'Username'},
  {key: 'type', label: 'Tip'},
  {key: 'our_delivery_percentage', label: 'Procent livrare', type: 'number'},
  // Commission on orders the restaurant delivers itself (no deliverer of ours
  // on the order); 0 = same as Procent livrare. See Invoices' deliveryPercentage.
  {key: 'restaurant_delivery_percentage', label: 'Procent livrare proprie restaurant', type: 'number'},
  {key: 'promotions', label: 'Promoții'},
  {key: 'closed_label', label: 'Etichetă închis'},
  {key: 'tags', label: 'Etichete (separate prin virgulă)'},
  {key: 'pickup_percentage', label: 'Procent ridicare personală', type: 'number'},
  {key: 'admin_fee', label: 'Taxă admin', type: 'number'},
  {key: 'ebriza_client_id', label: 'Ebriza Client ID'},
  {key: 'company_id', label: 'Companie', type: 'number'},
];

const SWITCH_FIELDS = [
  {key: 'available', label: 'Disponibil'},
  {key: 'our_delivery', label: 'Livrare proprie'},
  {key: 'pickup', label: 'Ridicare personală'},
  {key: 'online_payment', label: 'Plată online'},
  {key: 'scheduled_orders', label: 'Comenzi programate'},
  {key: 'voucher', label: 'Voucher'},
  {key: 'create_invoice', label: 'Generează factură'},
];

// available is deliberately kept out of the modal: it's toggled from the
// table's own Disponibil column, and a new restaurant must always start
// unavailable. It stays in SWITCH_FIELDS (and so in restaurantForm) so an
// edit save round-trips the existing value instead of the backend's
// unconditional field update resetting it to false.
const VISIBLE_SWITCH_FIELDS = SWITCH_FIELDS.filter((f) => f.key !== 'available');

const NUMERIC_KEYS = [
  ...TEXT_FIELDS.filter((f) => f.type === 'number').map((f) => f.key),
  ...ADDRESS_FIELDS.filter((f) => f.type === 'number').map((f) => f.key),
  'latitude',
  'longitude',
];

function emptyRestaurantForm() {
  const form = {logo: '', cover_photo: '', name: '', description: '', slug: '', latitude: '', longitude: ''};
  ADDRESS_FIELDS.forEach((f) => {
    form[f.key] = '';
  });
  TEXT_FIELDS.forEach((f) => {
    form[f.key] = '';
  });
  SWITCH_FIELDS.forEach((f) => {
    form[f.key] = false;
  });
  return form;
}

const COMPANY_FIELDS = [
  {key: 'name', label: 'Nume'},
  {key: 'cif', label: 'CIF'},
  {key: 'email', label: 'Email'},
  {key: 'iban', label: 'IBAN'},
  {key: 'iban_swift', label: 'IBAN Swift'},
];

function emptyCompanyForm() {
  const form = {send_payment: true};
  COMPANY_FIELDS.forEach((f) => {
    form[f.key] = '';
  });
  return form;
}

// Each delivery area is a circle (radius in km) around the restaurant's own
// coordinates, scoped to one city. Overlapping areas within the same city
// are resolved backend-side by picking the smallest radius that still
// covers the customer, so areas are naturally read as nested rings.
const DELIVERY_AREA_COLORS = ['#DA7D46', '#4285F4', '#34A853', '#EA4335', '#9C27B0', '#00ACC1', '#F06292', '#8D6E63'];

const DELIVERY_AREA_NUMBER_FIELDS = [
  {key: 'area', label: 'Rază (km)', step: '0.1'},
  {key: 'minimum_order_price', label: 'Comandă minimă'},
  {key: 'delivery_time', label: 'Timp livrare (min)'},
  {key: 'delivery_fee', label: 'Cost livrare'},
  {key: 'free_delivery_over', label: 'Livrare gratuită peste'},
  {key: 'app_tax', label: 'Taxă aplicație'},
];

function emptyDeliveryArea(key) {
  return {
    _key: key,
    id: null,
    city_id: '',
    area: '',
    minimum_order_price: '',
    delivery_time: '',
    delivery_fee: '',
    free_delivery_over: '',
    app_tax: '',
    available: true,
    close_for_village: false,
  };
}

export default class Restaurants extends React.Component {
  state = {
    restaurants: [],
    loading: false,
    lightboxSrc: null,
    isAdmin: false,
    includeDeleted: false,
    cities: [],
    selectedCity: "",
    searchTerm: "",
    restaurantDialogOpen: false,
    editingRestaurantId: null,
    restaurantForm: emptyRestaurantForm(),
    restaurantSaving: false,
    logoUploading: false,
    coverPhotoUploading: false,
    ebrizaEnabled: false,
    scheduleDialogOpen: false,
    scheduleRestaurant: null,
    scheduleForm: null,
    scheduleLoading: false,
    scheduleSaving: false,
    companies: [],
    companyDialogOpen: false,
    editingCompanyId: null,
    companyForm: emptyCompanyForm(),
    companySaving: false,
    adminCities: [],
    deliveryAreasDialogOpen: false,
    deliveryAreasRestaurant: null,
    deliveryAreas: [],
    deliveryAreasLoading: false,
    deliveryAreasSaving: false,
  };

  deliveryAreaKeyCounter = 0;
  deliveryAreaCircles = {};

  componentDidMount() {
    const isAdmin = localStorage.getItem("admin") === "true";
    this.setState({isAdmin});
    this.getRestaurants();
    if (isAdmin) {
      this.getCities();
      this.getCompanies();
    }
  }

  componentWillUnmount() {
  }

  async getRestaurants() {
    this.setState({loading: true});
    // const baseUrl = 'http://localhost:8080/';
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurants`, {
      params: this.state.includeDeleted ? {includeDeleted: true} : {},
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    this.setState({restaurants: response.data, loading: false});
  }

  async getCities() {
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurants/cities`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    this.setState({cities: response.data || []});
  }

  async getCompanies() {
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/restaurant-companies`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    this.setState({companies: response.data || []});
  }

  toggleIncludeDeleted = async () => {
    this.setState({includeDeleted: !this.state.includeDeleted}, () => {
      this.getRestaurants();
    });
  };

  async updateRestaurantStatus(restaurantId, status) {
    this.setState({loading: true});
    // const baseUrl = 'http://localhost:8080/';
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    await axios.default.put(`${baseUrl}v1/admin/restaurant/${restaurantId}`, {available: status}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  openCreateRestaurantDialog = () => {
    this.setState({
      restaurantDialogOpen: true,
      editingRestaurantId: null,
      restaurantForm: emptyRestaurantForm(),
      ebrizaEnabled: false,
    });
  };

  openEditRestaurantDialog = (restaurant) => {
    const form = emptyRestaurantForm();
    Object.keys(form).forEach((key) => {
      if (typeof form[key] === 'boolean') {
        form[key] = !!restaurant[key];
      } else {
        form[key] = restaurant[key] ?? '';
      }
    });
    this.setState({
      restaurantDialogOpen: true,
      editingRestaurantId: restaurant.id,
      restaurantForm: form,
      ebrizaEnabled: !!form.ebriza_client_id,
    });
  };

  closeRestaurantDialog = () => {
    this.setState({restaurantDialogOpen: false, editingRestaurantId: null});
  };

  toggleEbrizaEnabled = (event) => {
    const checked = event.target.checked;
    this.setState({
      ebrizaEnabled: checked,
      restaurantForm: checked
        ? this.state.restaurantForm
        : {...this.state.restaurantForm, ebriza_client_id: ''},
    });
  };

  updateRestaurantFormField = (key) => (event) => {
    this.setState({restaurantForm: {...this.state.restaurantForm, [key]: event.target.value}});
  };

  toggleRestaurantFormField = (key) => (event) => {
    this.setState({restaurantForm: {...this.state.restaurantForm, [key]: event.target.checked}});
  };

  // "Livrare proprie" is shown and edited as the inverse of our_delivery:
  // our_delivery true means the platform delivers, so the restaurant's own
  // delivery is off, and vice versa.
  toggleOurDeliveryNegated = (event) => {
    this.setState({restaurantForm: {...this.state.restaurantForm, our_delivery: !event.target.checked}});
  };

  setRestaurantLatLng = (lat, lng) => {
    this.setState({restaurantForm: {...this.state.restaurantForm, latitude: lat, longitude: lng}});
  };

  mapContainerRef = (el) => {
    if (!el) {
      this.map = null;
      this.marker = null;
      return;
    }

    loadGoogleMaps().then((maps) => {
      if (!el.isConnected) {
        return;
      }
      const {latitude, longitude} = this.state.restaurantForm;
      const hasCoordinates = !!(parseFloat(latitude) && parseFloat(longitude));
      const lat = parseFloat(latitude) || DEFAULT_MAP_CENTER.lat;
      const lng = parseFloat(longitude) || DEFAULT_MAP_CENTER.lng;
      const center = {lat, lng};

      if (!hasCoordinates) {
        this.setRestaurantLatLng(lat, lng);
      }

      this.map = new maps.Map(el, {center, zoom: 15});
      this.marker = new maps.Marker({position: center, map: this.map, draggable: true});

      this.marker.addListener('dragend', () => {
        const position = this.marker.getPosition();
        this.setRestaurantLatLng(position.lat(), position.lng());
      });

      this.map.addListener('click', (event) => {
        this.marker.setPosition(event.latLng);
        this.setRestaurantLatLng(event.latLng.lat(), event.latLng.lng());
      });
    }).catch(() => {
    });
  };

  uploadLogo = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.setState({logoUploading: true});
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const response = await axios.default.post(`${baseUrl}v1/admin/restaurants/logo`, {image: base64}, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({restaurantForm: {...this.state.restaurantForm, logo: response.data.url}});
    } catch (e) {
      alert("Eroare la încărcarea logo-ului.");
    } finally {
      this.setState({logoUploading: false});
    }
  };

  uploadCoverPhoto = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.setState({coverPhotoUploading: true});
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const response = await axios.default.post(`${baseUrl}v1/admin/restaurants/cover-photo`, {image: base64}, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({restaurantForm: {...this.state.restaurantForm, cover_photo: response.data.url}});
    } catch (e) {
      alert("Eroare la încărcarea fotografiei de copertă.");
    } finally {
      this.setState({coverPhotoUploading: false});
    }
  };

  saveRestaurant = async () => {
    const {editingRestaurantId, restaurantForm} = this.state;
    const payload = {...restaurantForm};
    NUMERIC_KEYS.forEach((key) => {
      payload[key] = parseFloat(payload[key]) || 0;
    });

    this.setState({restaurantSaving: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const headers = {Authorization: `Bearer ${token}`};
      if (editingRestaurantId) {
        await axios.default.put(`${baseUrl}v1/admin/restaurant/${editingRestaurantId}/details`, payload, {headers});
      } else {
        await axios.default.post(`${baseUrl}v1/admin/restaurant`, payload, {headers});
      }
      await this.getRestaurants();
      this.closeRestaurantDialog();
    } catch (e) {
      if (e.response && e.response.data && e.response.data.error === 'slug-taken') {
        alert("Linkul (slug) este folosit deja de alt restaurant.");
        return;
      }
      alert("Eroare la salvarea restaurantului.");
    } finally {
      this.setState({restaurantSaving: false});
    }
  };

  deleteRestaurantRow = async (restaurant) => {
    if (!window.confirm(`Ștergi restaurantul ${restaurant.name}?`)) {
      return;
    }
    this.setState({loading: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      await axios.default.delete(`${baseUrl}v1/admin/restaurant/${restaurant.id}`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      await this.getRestaurants();
    } catch (e) {
      alert("Eroare la ștergerea restaurantului.");
      this.setState({loading: false});
    }
  };

  recoverRestaurantRow = async (restaurant) => {
    this.setState({loading: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      await axios.default.post(`${baseUrl}v1/admin/restaurant/${restaurant.id}/recover`, {}, {
        headers: {Authorization: `Bearer ${token}`},
      });
      await this.getRestaurants();
    } catch (e) {
      alert("Eroare la recuperarea restaurantului.");
      this.setState({loading: false});
    }
  };

  openScheduleDialog = async (restaurant) => {
    this.setState({
      scheduleDialogOpen: true,
      scheduleRestaurant: restaurant,
      scheduleForm: null,
      scheduleLoading: true,
    });
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/restaurant/${restaurant.id}/schedule`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      const form = {};
      SCHEDULE_DAYS.forEach(({key}) => {
        form[key] = parseScheduleValue(response.data[key]);
      });
      this.setState({scheduleForm: form, scheduleLoading: false});
    } catch (e) {
      alert("Eroare la încărcarea programului.");
      this.setState({scheduleDialogOpen: false, scheduleLoading: false});
    }
  };

  closeScheduleDialog = () => {
    this.setState({scheduleDialogOpen: false, scheduleRestaurant: null, scheduleForm: null});
  };

  updateScheduleDayTime = (day, field) => (event) => {
    this.setState({
      scheduleForm: {
        ...this.state.scheduleForm,
        [day]: {...this.state.scheduleForm[day], [field]: event.target.value},
      },
    });
  };

  toggleScheduleDayClosed = (day) => (event) => {
    this.setState({
      scheduleForm: {
        ...this.state.scheduleForm,
        [day]: {...this.state.scheduleForm[day], closed: event.target.checked},
      },
    });
  };

  saveSchedule = async () => {
    const {scheduleRestaurant, scheduleForm} = this.state;
    const payload = {};
    SCHEDULE_DAYS.forEach(({key}) => {
      const day = scheduleForm[key];
      payload[key] = day.closed ? CLOSED_DAY_LABEL : `${day.open}-${day.close}`;
    });

    this.setState({scheduleSaving: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      await axios.default.put(`${baseUrl}v1/admin/restaurant/${scheduleRestaurant.id}/schedule`, payload, {
        headers: {Authorization: `Bearer ${token}`},
      });
      await this.getRestaurants();
      this.closeScheduleDialog();
    } catch (e) {
      alert("Eroare la salvarea programului.");
    } finally {
      this.setState({scheduleSaving: false});
    }
  };

  openCreateCompanyDialog = () => {
    this.setState({
      companyDialogOpen: true,
      editingCompanyId: null,
      companyForm: emptyCompanyForm(),
    });
  };

  openEditCompanyDialog = () => {
    const company = this.state.companies.find((c) => c.id === Number(this.state.restaurantForm.company_id));
    if (!company) {
      return;
    }
    const form = emptyCompanyForm();
    Object.keys(form).forEach((key) => {
      if (typeof form[key] === 'boolean') {
        form[key] = !!company[key];
      } else {
        form[key] = company[key] ?? '';
      }
    });
    this.setState({companyDialogOpen: true, editingCompanyId: company.id, companyForm: form});
  };

  closeCompanyDialog = () => {
    this.setState({companyDialogOpen: false, editingCompanyId: null});
  };

  updateCompanyFormField = (key) => (event) => {
    this.setState({companyForm: {...this.state.companyForm, [key]: event.target.value}});
  };

  toggleCompanyFormField = (key) => (event) => {
    this.setState({companyForm: {...this.state.companyForm, [key]: event.target.checked}});
  };

  saveCompany = async () => {
    const {editingCompanyId, companyForm} = this.state;

    this.setState({companySaving: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const headers = {Authorization: `Bearer ${token}`};
      let savedCompany;
      if (editingCompanyId) {
        const response = await axios.default.put(`${baseUrl}v1/admin/restaurant-companies/${editingCompanyId}`, companyForm, {headers});
        savedCompany = response.data;
      } else {
        const response = await axios.default.post(`${baseUrl}v1/admin/restaurant-companies`, companyForm, {headers});
        savedCompany = response.data;
      }
      await this.getCompanies();
      this.setState({
        restaurantForm: {...this.state.restaurantForm, company_id: savedCompany.id},
      });
      this.closeCompanyDialog();
    } catch (e) {
      alert("Eroare la salvarea companiei.");
    } finally {
      this.setState({companySaving: false});
    }
  };

  getAdminCities = async () => {
    const baseUrl = 'https://api.appetito.ro/';
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/cities`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({adminCities: response.data || []});
  };

  openDeliveryAreasDialog = async (restaurant) => {
    this.deliveryAreaCircles = {};
    this.setState({
      deliveryAreasDialogOpen: true,
      deliveryAreasRestaurant: restaurant,
      deliveryAreas: [],
      deliveryAreasLoading: true,
    });
    try {
      if (!this.state.adminCities.length) {
        await this.getAdminCities();
      }
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/restaurant/${restaurant.id}/delivery-areas`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      const deliveryAreas = (response.data || []).map((area) => {
        this.deliveryAreaKeyCounter += 1;
        return {...area, _key: this.deliveryAreaKeyCounter};
      });
      this.setState({deliveryAreas, deliveryAreasLoading: false}, () => this.renderDeliveryAreaCircles(true));
    } catch (e) {
      alert("Eroare la încărcarea zonelor de livrare.");
      this.setState({deliveryAreasLoading: false});
    }
  };

  closeDeliveryAreasDialog = () => {
    this.setState({deliveryAreasDialogOpen: false, deliveryAreasRestaurant: null, deliveryAreas: []});
    this.deliveryAreasMap = null;
    this.deliveryAreasMaps = null;
    this.deliveryAreaMarker = null;
    this.deliveryAreaCircles = {};
  };

  deliveryAreasMapContainerRef = (el) => {
    if (!el) {
      this.deliveryAreasMap = null;
      this.deliveryAreasMaps = null;
      this.deliveryAreaMarker = null;
      return;
    }

    loadGoogleMaps().then((maps) => {
      if (!el.isConnected) {
        return;
      }
      const {deliveryAreasRestaurant} = this.state;
      const lat = parseFloat(deliveryAreasRestaurant?.latitude) || DEFAULT_MAP_CENTER.lat;
      const lng = parseFloat(deliveryAreasRestaurant?.longitude) || DEFAULT_MAP_CENTER.lng;
      const center = {lat, lng};

      this.deliveryAreasMaps = maps;
      this.deliveryAreasMap = new maps.Map(el, {center, zoom: 13});
      this.deliveryAreaMarker = new maps.Marker({position: center, map: this.deliveryAreasMap});
      this.renderDeliveryAreaCircles(true);
    }).catch(() => {
    });
  };

  renderDeliveryAreaCircles = (fitBounds) => {
    if (!this.deliveryAreasMap || !this.deliveryAreasMaps) {
      return;
    }
    const {deliveryAreasRestaurant, deliveryAreas} = this.state;
    if (!deliveryAreasRestaurant) {
      return;
    }
    const center = {
      lat: parseFloat(deliveryAreasRestaurant.latitude) || DEFAULT_MAP_CENTER.lat,
      lng: parseFloat(deliveryAreasRestaurant.longitude) || DEFAULT_MAP_CENTER.lng,
    };
    const seenKeys = new Set();
    const bounds = new this.deliveryAreasMaps.LatLngBounds();
    let hasCircle = false;

    deliveryAreas.forEach((area, index) => {
      const key = String(area._key);
      seenKeys.add(key);
      const radiusMeters = (parseFloat(area.area) || 0) * 1000;
      const color = DELIVERY_AREA_COLORS[index % DELIVERY_AREA_COLORS.length];

      let circle = this.deliveryAreaCircles[key];
      if (!circle) {
        circle = new this.deliveryAreasMaps.Circle({
          map: this.deliveryAreasMap,
          center,
          radius: radiusMeters || 1,
          strokeColor: color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.12,
        });
        this.deliveryAreaCircles[key] = circle;
      } else {
        circle.setCenter(center);
        circle.setRadius(radiusMeters || 1);
        circle.setOptions({strokeColor: color, fillColor: color});
      }

      if (radiusMeters > 0) {
        hasCircle = true;
        bounds.union(circle.getBounds());
      }
    });

    Object.keys(this.deliveryAreaCircles).forEach((key) => {
      if (!seenKeys.has(key)) {
        this.deliveryAreaCircles[key].setMap(null);
        delete this.deliveryAreaCircles[key];
      }
    });

    if (fitBounds && hasCircle) {
      this.deliveryAreasMap.fitBounds(bounds);
    }
  };

  addDeliveryAreaRow = () => {
    this.deliveryAreaKeyCounter += 1;
    const deliveryAreas = [...this.state.deliveryAreas, emptyDeliveryArea(this.deliveryAreaKeyCounter)];
    this.setState({deliveryAreas}, this.renderDeliveryAreaCircles);
  };

  updateDeliveryAreaField = (index, key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    const deliveryAreas = [...this.state.deliveryAreas];
    deliveryAreas[index] = {...deliveryAreas[index], [key]: value};
    this.setState({deliveryAreas}, this.renderDeliveryAreaCircles);
  };

  deleteDeliveryAreaRow = (index) => async () => {
    const area = this.state.deliveryAreas[index];
    if (area.id) {
      if (!window.confirm("Ștergi această zonă de livrare?")) {
        return;
      }
      try {
        const baseUrl = 'https://api.appetito.ro/';
        const token = localStorage.getItem('access_token');
        await axios.default.delete(`${baseUrl}v1/admin/delivery-areas/${area.id}`, {
          headers: {Authorization: `Bearer ${token}`},
        });
      } catch (e) {
        alert("Eroare la ștergerea zonei de livrare.");
        return;
      }
    }
    const deliveryAreas = this.state.deliveryAreas.filter((_, i) => i !== index);
    this.setState({deliveryAreas}, this.renderDeliveryAreaCircles);
  };

  saveDeliveryAreas = async () => {
    const {deliveryAreasRestaurant, deliveryAreas} = this.state;
    if (deliveryAreas.some((area) => !area.city_id)) {
      alert("Alege un oraș pentru fiecare zonă de livrare.");
      return;
    }

    this.setState({deliveryAreasSaving: true});
    try {
      const baseUrl = 'https://api.appetito.ro/';
      const token = localStorage.getItem('access_token');
      const headers = {Authorization: `Bearer ${token}`};
      for (const area of deliveryAreas) {
        const payload = {
          city_id: parseInt(area.city_id, 10) || 0,
          area: parseFloat(area.area) || 0,
          minimum_order_price: parseFloat(area.minimum_order_price) || 0,
          delivery_time: parseInt(area.delivery_time, 10) || 0,
          delivery_fee: parseFloat(area.delivery_fee) || 0,
          free_delivery_over: parseFloat(area.free_delivery_over) || 0,
          app_tax: parseFloat(area.app_tax) || 0,
          available: !!area.available,
          close_for_village: !!area.close_for_village,
        };
        if (area.id) {
          await axios.default.put(`${baseUrl}v1/admin/delivery-areas/${area.id}`, payload, {headers});
        } else {
          await axios.default.post(`${baseUrl}v1/admin/restaurant/${deliveryAreasRestaurant.id}/delivery-areas`, payload, {headers});
        }
      }
      await this.openDeliveryAreasDialog(deliveryAreasRestaurant);
    } catch (e) {
      alert("Eroare la salvarea zonelor de livrare.");
    } finally {
      this.setState({deliveryAreasSaving: false});
    }
  };

  render() {
    const {
      restaurants,
      loading,
      lightboxSrc,
      isAdmin,
      includeDeleted,
      cities,
      selectedCity,
      searchTerm,
      restaurantDialogOpen,
      editingRestaurantId,
      restaurantForm,
      restaurantSaving,
      logoUploading,
      coverPhotoUploading,
      ebrizaEnabled,
      scheduleDialogOpen,
      scheduleRestaurant,
      scheduleForm,
      scheduleLoading,
      scheduleSaving,
      companies,
      companyDialogOpen,
      editingCompanyId,
      companyForm,
      companySaving,
      adminCities,
      deliveryAreasDialogOpen,
      deliveryAreasRestaurant,
      deliveryAreas,
      deliveryAreasLoading,
      deliveryAreasSaving,
    } = this.state;

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredRestaurants = restaurants.filter((restaurant) => {
      if (selectedCity && restaurant.city !== selectedCity) {
        return false;
      }
      if (normalizedSearch && !restaurant.name?.toLowerCase().includes(normalizedSearch)) {
        return false;
      }
      return true;
    });

    return (
      <div className={"restaurants-container"}>
        {isAdmin && (
          <div className="admin-toolbar">
            <div className="admin-toolbar-left">
              <FormControlLabel
                control={
                  <Switch
                    checked={includeDeleted}
                    onChange={this.toggleIncludeDeleted}
                    color="primary"
                  />
                }
                label="Arată toate restaurantele (inclusiv șterse)"
              />
              <Select
                value={selectedCity}
                onChange={(event) => this.setState({selectedCity: event.target.value})}
                displayEmpty
                style={{marginLeft: 24, minWidth: 220}}
              >
                <MenuItem value="">Toate Restaurantele</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </div>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon/>}
              onClick={this.openCreateRestaurantDialog}
            >
              Adaugă Restaurant
            </Button>
          </div>
        )}

        <div className="tables-row">
          <table className="restaurants-table">
            <thead>
            <tr>
              {isAdmin && <th className="restaurants-table-center">Nr</th>}
              <th>
                <div className="restaurants-table-search-header">
                  <span>Restaurant</span>
                  <TextField
                    value={searchTerm}
                    onChange={(event) => this.setState({searchTerm: event.target.value})}
                    placeholder="Caută după nume..."
                    className="restaurants-table-search-input"
                    margin="dense"
                  />
                </div>
              </th>
              {isAdmin && <th className="restaurants-table-center">Sold</th>}
              <th>Disponibil</th>
              <th>Orar azi</th>
              <th>Telefon</th>
              <th className="restaurants-table-center">Online</th>
              {isAdmin && <th>Acțiuni</th>}
            </tr>
            </thead>
            <tbody>
            {filteredRestaurants.map((restaurant, i) => {
              return (
                <tr key={restaurant.id} className={restaurant.deleted ? "restaurants-row-deleted" : ""}>
                  {isAdmin && <td className="restaurants-table-center">{i + 1}</td>}
                  <td>{restaurant.name} {restaurant.city}</td>
                  {isAdmin && (() => {
                    const company = companies.find((c) => c.id === restaurant.company_id);
                    return (
                      <td className={`restaurants-table-center${company && company.sold < 0 ? ' restaurants-sold-negative' : ''}`}>
                        {company ? `${company.sold.toFixed(2)} lei` : '—'}
                      </td>
                    );
                  })()}
                  <td><Switch checked={restaurant.available} disabled={restaurant.deleted}
                                                 onChange={async () => {
                                                   await this.updateRestaurantStatus(restaurant.id, !restaurant.available)
                                                   await this.getRestaurants();
                                                 }}/></td>
                  <td>{restaurant.open_hours === '00:00-00:00' ? 'Închis' : restaurant.open_hours}</td>
                  <td>{restaurant.phone}</td>
                  <td className={`restaurants-table-center${
                    isRestaurantFreshlyOnline(restaurant.last_seen)
                      ? ' restaurants-online-fresh'
                      : isRestaurantOffline(restaurant.last_seen) ? ' restaurants-online-offline' : ''
                  }`}>
                    {isRestaurantFreshlyOnline(restaurant.last_seen) ? 'Online' : formatLastSeen(restaurant.last_seen)}
                  </td>
                  {isAdmin && (
                    <td>
                      <IconButton size="small" onClick={() => this.openEditRestaurantDialog(restaurant)}>
                        <EditIcon fontSize="small"/>
                      </IconButton>
                      <IconButton size="small" title="Setează program"
                                  onClick={() => this.openScheduleDialog(restaurant)}>
                        <ScheduleIcon fontSize="small"/>
                      </IconButton>
                      <IconButton size="small" title="Zone de livrare"
                                  onClick={() => this.openDeliveryAreasDialog(restaurant)}>
                        <RoomIcon fontSize="small"/>
                      </IconButton>
                      {restaurant.deleted ? (
                        <IconButton size="small" onClick={() => this.recoverRestaurantRow(restaurant)}>
                          <RestoreIcon fontSize="small"/>
                        </IconButton>
                      ) : (
                        <IconButton size="small" onClick={() => this.deleteRestaurantRow(restaurant)}>
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
            </tbody>
          </table>
        </div>

        {isAdmin && (
          <Dialog open={restaurantDialogOpen} onClose={this.closeRestaurantDialog} fullWidth maxWidth="md">
            <DialogTitle>{editingRestaurantId ? "Editează Restaurant" : "Adaugă Restaurant"}</DialogTitle>
            <DialogContent>
              <div className="restaurant-header">
                <div className="restaurant-logo-section">
                  <div className="restaurant-logo-preview">
                    {restaurantForm.logo ? (
                      <img
                        src={restaurantForm.logo}
                        alt="Logo"
                        onClick={() => this.setState({lightboxSrc: restaurantForm.logo})}
                        style={{cursor: 'pointer'}}
                      />
                    ) : (
                      <span className="restaurant-logo-placeholder">Fără logo</span>
                    )}
                  </div>
                  <label className="restaurant-logo-upload-button">
                    <input type="file" accept="image/*" onChange={this.uploadLogo} disabled={logoUploading}/>
                    {logoUploading ? "Se încarcă..." : "Încarcă logo"}
                  </label>
                </div>
                <div className="restaurant-logo-section">
                  <div className="restaurant-logo-preview">
                    {restaurantForm.cover_photo ? (
                      <img
                        src={restaurantForm.cover_photo}
                        alt="Fotografie copertă"
                        onClick={() => this.setState({lightboxSrc: restaurantForm.cover_photo})}
                        style={{cursor: 'pointer'}}
                      />
                    ) : (
                      <span className="restaurant-logo-placeholder">Fără copertă</span>
                    )}
                  </div>
                  <label className="restaurant-logo-upload-button">
                    <input type="file" accept="image/*" onChange={this.uploadCoverPhoto} disabled={coverPhotoUploading}/>
                    {coverPhotoUploading ? "Se încarcă..." : "Încarcă copertă"}
                  </label>
                </div>
                <div className="restaurant-name-description-col">
                  <TextField
                    label="Nume"
                    value={restaurantForm.name}
                    onChange={this.updateRestaurantFormField('name')}
                    margin="dense"
                    InputProps={{className: "restaurant-name-input"}}
                    InputLabelProps={{className: "restaurant-name-label"}}
                  />
                  <TextField
                    label="Descriere"
                    value={restaurantForm.description}
                    onChange={this.updateRestaurantFormField('description')}
                    margin="dense"
                    multiline
                    rows={2}
                  />
                  {/* The site's /restaurant/:slug link. Left empty, the API
                      generates it from the name and city. */}
                  <TextField
                    label="Link site (slug)"
                    value={restaurantForm.slug}
                    onChange={this.updateRestaurantFormField('slug')}
                    margin="dense"
                    placeholder="ex. american-crust-turda"
                    helperText={`appetito.ro/restaurant/${restaurantForm.slug || '(generat din nume și oraș)'}`}
                  />
                </div>
              </div>

              <div className="restaurant-section-title">Adresă</div>
              <div className="restaurant-address-grid">
                <div className="restaurant-map-container" ref={this.mapContainerRef}/>
                <div className="restaurant-address-col">
                  {ADDRESS_FIELDS_COL2.map((field) => (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type={field.type || 'text'}
                      value={restaurantForm[field.key]}
                      onChange={this.updateRestaurantFormField(field.key)}
                      margin="dense"
                      fullWidth
                    />
                  ))}
                </div>
                <div className="restaurant-address-col">
                  {ADDRESS_FIELDS_COL3.map((field) => (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type={field.type || 'text'}
                      value={restaurantForm[field.key]}
                      onChange={this.updateRestaurantFormField(field.key)}
                      margin="dense"
                      fullWidth
                    />
                  ))}
                </div>
              </div>

              <div className="restaurant-section-title">Informații</div>
              <div className="restaurant-form-grid">
                {TEXT_FIELDS.map((field) => (
                  field.key === 'type' ? (
                    <TextField
                      key={field.key}
                      select
                      label={field.label}
                      value={restaurantForm.type}
                      onChange={this.updateRestaurantFormField('type')}
                      margin="dense"
                    >
                      <MenuItem value="market">Market</MenuItem>
                      <MenuItem value="restaurant">Restaurant</MenuItem>
                      {restaurantForm.type === 'multi' && (
                        <MenuItem value="multi" disabled>Multi</MenuItem>
                      )}
                    </TextField>
                  ) : field.key === 'ebriza_client_id' ? (
                    <div key={field.key} className="restaurant-ebriza-field">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ebrizaEnabled}
                            onChange={this.toggleEbrizaEnabled}
                            color="primary"
                          />
                        }
                        label="Integrare Ebriza"
                      />
                      {ebrizaEnabled && (
                        <TextField
                          label={field.label}
                          value={restaurantForm.ebriza_client_id}
                          onChange={this.updateRestaurantFormField('ebriza_client_id')}
                          margin="dense"
                          fullWidth
                        />
                      )}
                    </div>
                  ) : field.key === 'company_id' ? (
                    <div key={field.key} className="restaurant-company-field">
                      <TextField
                        select
                        label={field.label}
                        value={restaurantForm.company_id || ''}
                        onChange={this.updateRestaurantFormField('company_id')}
                        margin="dense"
                        fullWidth
                      >
                        <MenuItem value="">Fără companie</MenuItem>
                        {companies.map((company) => (
                          <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                        ))}
                      </TextField>
                      <IconButton size="small" title="Editează compania"
                                  disabled={!restaurantForm.company_id}
                                  onClick={this.openEditCompanyDialog}>
                        <EditIcon fontSize="small"/>
                      </IconButton>
                      <IconButton size="small" title="Adaugă companie" onClick={this.openCreateCompanyDialog}>
                        <AddIcon fontSize="small"/>
                      </IconButton>
                    </div>
                  ) : (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type={field.type || 'text'}
                      value={restaurantForm[field.key]}
                      onChange={this.updateRestaurantFormField(field.key)}
                      margin="dense"
                    />
                  )
                ))}
              </div>
              <div className="restaurant-form-switches">
                {VISIBLE_SWITCH_FIELDS.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Switch
                        checked={field.key === 'our_delivery' ? !restaurantForm.our_delivery : restaurantForm[field.key]}
                        onChange={field.key === 'our_delivery' ? this.toggleOurDeliveryNegated : this.toggleRestaurantFormField(field.key)}
                        color="primary"
                      />
                    }
                    label={field.label}
                  />
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.closeRestaurantDialog} disabled={restaurantSaving}>
                Anulează
              </Button>
              <Button
                onClick={this.saveRestaurant}
                color="primary"
                variant="contained"
                disabled={restaurantSaving}
              >
                Salvează
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {isAdmin && (
          <Dialog open={scheduleDialogOpen} onClose={this.closeScheduleDialog} fullWidth maxWidth="sm">
            <DialogTitle>Program{scheduleRestaurant ? ` — ${scheduleRestaurant.name}` : ''}</DialogTitle>
            <DialogContent>
              {scheduleLoading || !scheduleForm ? (
                <div style={{padding: 24, textAlign: 'center'}}>Se încarcă...</div>
              ) : (
                <div className="schedule-grid">
                  {SCHEDULE_DAYS.map(({key, label}) => {
                    const day = scheduleForm[key];
                    return (
                      <div className="schedule-row" key={key}>
                        <span className="schedule-day-label">{label}</span>
                        <TextField
                          type="time"
                          label="Deschidere"
                          value={day.open}
                          onChange={this.updateScheduleDayTime(key, 'open')}
                          disabled={day.closed}
                          InputLabelProps={{shrink: true}}
                          inputProps={{step: 60}}
                          margin="dense"
                        />
                        <TextField
                          type="time"
                          label="Închidere"
                          value={day.close}
                          onChange={this.updateScheduleDayTime(key, 'close')}
                          disabled={day.closed}
                          InputLabelProps={{shrink: true}}
                          inputProps={{step: 60}}
                          margin="dense"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={day.closed}
                              onChange={this.toggleScheduleDayClosed(key)}
                              color="primary"
                            />
                          }
                          label="Închis"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={this.closeScheduleDialog} disabled={scheduleSaving}>
                Anulează
              </Button>
              <Button
                onClick={this.saveSchedule}
                color="primary"
                variant="contained"
                disabled={scheduleSaving || !scheduleForm}
              >
                Salvează
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {isAdmin && (
          <Dialog open={deliveryAreasDialogOpen} onClose={this.closeDeliveryAreasDialog} fullWidth maxWidth="lg">
            <DialogTitle>Zone de livrare{deliveryAreasRestaurant ? ` — ${deliveryAreasRestaurant.name}` : ''}</DialogTitle>
            <DialogContent>
              <div className="delivery-areas-map-container" ref={this.deliveryAreasMapContainerRef}/>

              {deliveryAreasLoading ? (
                <div style={{padding: 24, textAlign: 'center'}}>Se încarcă...</div>
              ) : (
                <div className="delivery-areas-list">
                  {deliveryAreas.length === 0 && (
                    <div className="delivery-areas-empty">Nicio zonă de livrare configurată.</div>
                  )}
                  {deliveryAreas.map((area, index) => (
                    <div
                      className="delivery-area-row"
                      key={area._key}
                      style={{borderLeftColor: DELIVERY_AREA_COLORS[index % DELIVERY_AREA_COLORS.length]}}
                    >
                      <div className="delivery-area-row-header">
                        <span
                          className="delivery-area-color-dot"
                          style={{backgroundColor: DELIVERY_AREA_COLORS[index % DELIVERY_AREA_COLORS.length]}}
                        />
                        <TextField
                          select
                          label="Oraș"
                          value={area.city_id || ''}
                          onChange={this.updateDeliveryAreaField(index, 'city_id')}
                          margin="dense"
                          className="delivery-area-city-select"
                        >
                          <MenuItem value="">Alege oraș</MenuItem>
                          {adminCities.map((city) => (
                            <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                          ))}
                        </TextField>
                        <IconButton size="small" title="Șterge zona" onClick={this.deleteDeliveryAreaRow(index)}>
                          <DeleteIcon fontSize="small"/>
                        </IconButton>
                      </div>
                      <div className="delivery-area-fields-grid">
                        {DELIVERY_AREA_NUMBER_FIELDS.map((field) => (
                          <TextField
                            key={field.key}
                            label={field.label}
                            type="number"
                            inputProps={field.step ? {step: field.step} : undefined}
                            value={area[field.key]}
                            onChange={this.updateDeliveryAreaField(index, field.key)}
                            margin="dense"
                          />
                        ))}
                      </div>
                      <div className="delivery-area-switches">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={area.available}
                              onChange={this.updateDeliveryAreaField(index, 'available')}
                              color="primary"
                            />
                          }
                          label="Disponibil"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={area.close_for_village}
                              onChange={this.updateDeliveryAreaField(index, 'close_for_village')}
                              color="primary"
                            />
                          }
                          label="Închide odată cu satele"
                        />
                      </div>
                    </div>
                  ))}
                  <Button startIcon={<AddIcon/>} onClick={this.addDeliveryAreaRow}>
                    Adaugă zonă
                  </Button>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={this.closeDeliveryAreasDialog} disabled={deliveryAreasSaving}>
                Anulează
              </Button>
              <Button
                onClick={this.saveDeliveryAreas}
                color="primary"
                variant="contained"
                disabled={deliveryAreasSaving || deliveryAreasLoading}
              >
                Salvează
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {isAdmin && (
          <Dialog open={companyDialogOpen} onClose={this.closeCompanyDialog} fullWidth maxWidth="xs">
            <DialogTitle>{editingCompanyId ? "Editează Companie" : "Adaugă Companie"}</DialogTitle>
            <DialogContent>
              {COMPANY_FIELDS.map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  value={companyForm[field.key]}
                  onChange={this.updateCompanyFormField(field.key)}
                  margin="dense"
                  fullWidth
                />
              ))}
              <FormControlLabel
                control={
                  <Switch
                    checked={companyForm.send_payment}
                    onChange={this.toggleCompanyFormField('send_payment')}
                    color="primary"
                  />
                }
                label="Trimite plată"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={this.closeCompanyDialog} disabled={companySaving}>
                Anulează
              </Button>
              <Button
                onClick={this.saveCompany}
                color="primary"
                variant="contained"
                disabled={companySaving}
              >
                Salvează
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {loading &&
        <div className={'loader-container'}>
          <div className={'loader'}/>
        </div>}

        <ImageLightbox src={lightboxSrc} onClose={() => this.setState({lightboxSrc: null})}/>
      </div>
    )
  }
}
